// Settings utility functions for managing configuration values in local storage

// Default configuration values
export const DEFAULT_SETTINGS = {
  defaultStopLossPercentage: -10,
  defaultTargetPercentage: 5,
  lotSize: 75,
  autoRefreshInterval: 0,
  positionSizingStrategy: 'conservative', // conservative, moderate, aggressive
  maxMarginUtilization: 30, // percentage
  defaultRiskPercentage: 2, // percentage of portfolio to risk per trade
};

// Storage key
const SETTINGS_STORAGE_KEY = 'glyder_app_settings';

/**
 * Get all settings from localStorage
 * @returns {Object} Settings object with defaults applied
 */
export const getSettings = () => {
  try {
    const stored = localStorage.getItem(SETTINGS_STORAGE_KEY);
    if (stored) {
      const parsedSettings = JSON.parse(stored);
      // Merge with defaults to ensure all keys exist
      return { ...DEFAULT_SETTINGS, ...parsedSettings };
    }
  } catch (error) {
    console.error('Error reading settings from localStorage:', error);
  }
  return DEFAULT_SETTINGS;
};

/**
 * Save settings to localStorage
 * @param {Object} settings - Settings object to save
 */
export const saveSettings = (settings) => {
  try {
    const currentSettings = getSettings();
    const updatedSettings = { ...currentSettings, ...settings };
    localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(updatedSettings));
    return updatedSettings;
  } catch (error) {
    console.error('Error saving settings to localStorage:', error);
    throw error;
  }
};

/**
 * Get a specific setting value
 * @param {string} key - Setting key
 * @returns {any} Setting value or default
 */
export const getSetting = (key) => {
  const settings = getSettings();
  return settings[key];
};

/**
 * Update a specific setting
 * @param {string} key - Setting key
 * @param {any} value - Setting value
 */
export const updateSetting = (key, value) => {
  const settings = getSettings();
  settings[key] = value;
  return saveSettings(settings);
};

/**
 * Reset settings to defaults
 */
export const resetSettings = () => {
  try {
    localStorage.removeItem(SETTINGS_STORAGE_KEY);
    return DEFAULT_SETTINGS;
  } catch (error) {
    console.error('Error resetting settings:', error);
    throw error;
  }
};

/**
 * Export settings as JSON
 */
export const exportSettings = () => {
  const settings = getSettings();
  return JSON.stringify(settings, null, 2);
};

/**
 * Import settings from JSON string
 * @param {string} jsonString - JSON string of settings
 */
export const importSettings = (jsonString) => {
  try {
    const importedSettings = JSON.parse(jsonString);
    return saveSettings(importedSettings);
  } catch (error) {
    console.error('Error importing settings:', error);
    throw error;
  }
};

/**
 * Validate settings object
 * @param {Object} settings - Settings to validate
 * @returns {Object} Validation result { isValid: boolean, errors: string[] }
 */
export const validateSettings = (settings) => {
  const errors = [];
  
  if (typeof settings.defaultStopLossPercentage !== 'number' || settings.defaultStopLossPercentage >= 0) {
    errors.push('Default stop loss percentage must be a negative number');
  }
  
  if (typeof settings.defaultTargetPercentage !== 'number' || settings.defaultTargetPercentage <= 0) {
    errors.push('Default target percentage must be a positive number');
  }
  
  if (typeof settings.lotSize !== 'number' || settings.lotSize <= 0) {
    errors.push('Lot size must be a positive number');
  }
  
  if (typeof settings.autoRefreshInterval !== 'number' || settings.autoRefreshInterval < 0) {
    errors.push('Auto refresh interval must be a non-negative number');
  }
  
  if (typeof settings.maxMarginUtilization !== 'number' || settings.maxMarginUtilization < 0 || settings.maxMarginUtilization > 100) {
    errors.push('Max margin utilization must be between 0 and 100');
  }
  
  if (typeof settings.defaultRiskPercentage !== 'number' || settings.defaultRiskPercentage <= 0 || settings.defaultRiskPercentage > 100) {
    errors.push('Default risk percentage must be between 0 and 100');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};
